<template lang="pug">
  v-form(ref="funding-information" lazy-validation): v-row.pa-5
    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text 19. Ich habe meinen Hauptwohnsitz in Deutschland (vor dem 01.Juli 2021 gemeldet) und bin berechtigt, hier meine freiberufliche oder auf Produktionsdauer begrenzte künstlerische Tätigkeit auszuüben
        p.text-preview {{ formData.hauptwohnsitz_deutschalnd ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.hauptwohnsitz_deutschalnd"
        label="19. Ich habe meinen Hauptwohnsitz in Deutschland (vor dem 01.Juli 2021 gemeldet) und bin berechtigt, hier meine freiberufliche oder auf Produktionsdauer begrenzte künstlerische Tätigkeit auszuüben"
        dense
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('hauptwohnsitz_deutschalnd', v)"
      )

    v-col(cols="12")
      label.d-block.primary--text.ml-8 20. Aktuell gültiger Reisepass UND aktuell gültige Meldebestätigung ODER Kopie des gültigen Personalausweises (Vorder- und Rückseite zusammen als ein PDF, max. 4MB)
      template(v-if="readonly")
        p.text-preview.ml-8 {{ formData.datei_reisepass ? formData.datei_reisepass.name : '' }}

      v-file-input(
        v-else
        v-model="formData.datei_reisepass"
        accept="application/pdf"
        dense
        outlined
        :rules="[ rules.required, rules.valid_file_size(4), rules.valid_file(fileCodes.ID_CARD) ]"
        @change="(v) => onSelectFile('datei_reisepass', v)"
      )

    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text 21. Ich habe einen Wahrnehmungsvertrag mit der GVL (optional)
        p.text-preview.mb-1 {{ formData.wahrnehmungsvertrag_gvl ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.wahrnehmungsvertrag_gvl"
        label="21. Ich habe einen Wahrnehmungsvertrag mit der GVL (optional)"
        dense
        @input="(v) => saveFieldValue('wahrnehmungsvertrag_gvl', v)"
      )

      p.hint-label.ml-8.mb-0(v-if="!formData.wahrnehmungsvertrag_gvl")
        | Sie haben noch keinen Wahrnehmungsvertrag mit der GVL? <a href="https://www.gvl.de/rechteinhaber/kuenstler/online-wahrnehmungsvertrag" target="blank">Hier können Sie Ihren Vertrag online abschließen</a>
        | – die Mitgliedschaft in der GVL ist natürlich kostenfrei. Sollten Sie soeben online einen Wahrnehmungsvertrag mit der GVL geschlossen haben, so finden Sie Ihre GVL-ID auf der als PDF übermittelten Vertragsausfertigung.

    v-col(v-if="formData.wahrnehmungsvertrag_gvl" cols="12")
      .ml-8(v-if="readonly")
        label.primary--text 21.a. Ich bin bei der GVL unter der folgenden Nummer registriert: Bitte GVL-ID oder Vertragsnummer angeben
        p.text-preview {{ formData.wahrnehmungsvertrag_gvl_id }}

      v-text-field.ml-8(
        v-else
        v-model="formData.wahrnehmungsvertrag_gvl_id"
        label="21.a. Ich bin bei der GVL unter der folgenden Nummer registriert: Bitte GVL-ID oder Vertragsnummer angeben"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('wahrnehmungsvertrag_gvl_id', v)"
      )

    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text 22. Ich bin bei der Künstlersozialkasse (KSK) (optional)
        p.text-preview {{ formData.kuenstlersozialkasse ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.kuenstlersozialkasse"
        label="22. Ich bin bei der Künstlersozialkasse (KSK) (optional)"
        dense
        @input="(v) => saveFieldValue('kuenstlersozialkasse', v)"
        @change="(v) => saveFieldValue('kuenstlersozialkasse', v)"
      )

    v-col(v-if="formData.kuenstlersozialkasse" cols="12")
      .ml-8(v-if="readonly")
        label.primary--text 22.a. Ich bin bei der KSK unter der folgenden Nummer registriert
        p.text-preview {{ formData.kuenstlersozialkasse_id }}

      v-text-field.ml-8(
        v-else
        v-model="formData.kuenstlersozialkasse_id"
        label="22.a. Ich bin bei der KSK unter der folgenden Nummer registriert"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('kuenstlersozialkasse_id', v)"
      )

    v-col(v-if="formData.kuenstlersozialkasse" cols="12")
      .ml-8(v-if="readonly")
        label.primary--text 22.b. Bitte KSK-Mitgliedsnachweis nicht älter als 2019 hochladen (PDF mit max. 4MB)
        p.text-preview {{ formData.datei_kuenstlersozialkasse ? formData.datei_kuenstlersozialkasse.name : '' }}

      v-file-input(
        v-else
        v-model="formData.datei_kuenstlersozialkasse"
        label="22.b. Bitte KSK-Mitgliedsnachweis nicht älter als 2019 hochladen (PDF mit max. 4MB)"
        outlined
        dense
        accept="application/pdf"
        :class="readonly && 'ml-8'"
        :rules="[ rules.required, rules.valid_file_size(4), rules.valid_file(fileCodes.KSK_PROOF) ]"
        @change="(v) => onSelectFile('datei_kuenstlersozialkasse', v)"
      )

    v-col.ml-8(v-if="!formData.kuenstlersozialkasse" cols="12")
      template(v-if="readonly")
        label.primary--text 22.a. Ich arbeite als selbständiger Künstler oder befinde mich in einer künstlerischen Ausbildung
        p.text-preview {{ getStarterById(formData.berufsanfaenger) }}

      v-select(
        v-else
        v-model="formData.berufsanfaenger"
        label="22.a. Ich arbeite als selbständiger Künstler oder befinde mich in einer künstlerischen Ausbildung"
        outlined
        dense
        placeholder="Bitte Auswählen!"
        persistent-placeholder
        :items="starters"
        :item-text="`text`"
        :item-value="`value`"
        :rules="[ rules.required_boolean ]"
        @input="(v) => saveFieldValue('berufsanfaenger', v)"
      )

    v-col.pt-0(v-if="!formData.kuenstlersozialkasse && formData.berufsanfaenger" cols="12")
      p.file-label.ml-8.mb-0 22.b. Bitte Ausbildungsnachweis wie bspw. Diplom hochladen, wobei der Abschluss frühestens 2018 erfolgt sein muss; alternativ können Nachweise über entsprechende Engagements ab dem Jahr 2018 hochgeladen werden, z.B. Verträge, Honorarabrechnungen (in einem PDF mit max. 4MB)

      p.text-preview.ml-8(v-if="readonly") {{ formData.datei_berufsanfaenger_ausbildungsnachweis ? formData.datei_berufsanfaenger_ausbildungsnachweis.name : '' }}
      v-file-input(
        v-else
        v-model="formData.datei_berufsanfaenger_ausbildungsnachweis"
        outlined
        dense
        accept="application/pdf"
        :rules="[ rules.required, rules.valid_file_size(4), rules.valid_file(fileCodes.PROOF_TRAINING) ]"
        @change="(v) => onSelectFile('datei_berufsanfaenger_ausbildungsnachweis', v)"
      )

    v-col.pt-0(v-if="!formData.kuenstlersozialkasse && formData.berufsanfaenger === false" cols="12")
      p.file-label.ml-8.mb-0 22.b. Aktueller Selbständigkeitsnachweis vom Finanzamt oder Sozialversicherungsnachweis, dessen Tätigkeitsschlüssel mit 94 (darstellende Berufe) beginnt lt. Klassifizierung der Berufe 2010 (PDF, max. 4MB)

      p.text-preview.ml-8(v-if="readonly") {{ formData.datei_selbststaendigkeitsnachweis ? formData.datei_selbststaendigkeitsnachweis.name : '' }}
      v-file-input(
        v-else
        v-model="formData.datei_selbststaendigkeitsnachweis"
        outlined
        dense
        accept="application/pdf"
        :rules="[ rules.required, rules.valid_file_size(4), rules.valid_file(fileCodes.PROOF_SELF_EMP) ]"
        @change="(v) => onSelectFile('datei_selbststaendigkeitsnachweis', v)"
      )

    v-col(:class="formData.gesamtbetrag_unter_60k && 'pb-0'" cols="12")
      template(v-if="readonly")
        label.primary--text 23. Mein zu versteuerndes Einkommen lag 2020 nicht über 60.000 €
        p.text-preview {{ formData.gesamtbetrag_unter_60k ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.gesamtbetrag_unter_60k"
        label="23. Mein zu versteuerndes Einkommen lag 2020 nicht über 60.000 €"
        dense
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('gesamtbetrag_unter_60k', v)"
      )

    v-col(v-if="formData.gesamtbetrag_unter_60k" cols="12" :class="!readonly && 'pt-0'")
      p.file-label.ml-8.mb-1
        | 23.a. Steuerbescheid 2020, wenn nicht vorhanden 2019 oder alternativ Bestätigung des Finanzamts, wonach die Kleinunternehmerregelung (§ 19 Abs. 1 UStG) in Anspruch genommen wird (nicht älter als 2019) oder Nichtveranlagungsbescheinigung des Finanzamts (gültig ab 01.01.2019 oder später) oder Grundsicherungsbescheid (für das gesamte Jahr 2020).<br>
        | Sollten auch diese Dokumente nicht beizubringen sein, kann auch eine ESt-Erklärung für das Jahr 2020 zusammen mit einer Bestätigung (ausgestellt durch einen Steuerberater oder eine vergleichbare steuerberatende Organisation mit Stempel und Unterschrift), wonach das zu versteuernden Einkommen <span class="text-decoration-underline">des Antragstellers</span> den Wert von 60.000 EUR nicht übersteigt eingereicht werden <strong class="text-decoration-underline">oder in einem verkürzten Verfahren</strong>: GVL-Formular „<a href="https://gvl.de/sites/default/files/2021-08/Einkommensbestaetigung_GVL_JW.pdf" target="_blank">Bestätigung des zu versteuernden Einkommens für 2020</a>“, wonach das zu versteuernde Einkommen <span class="text-decoration-underline">des Antragsstellers</span> den Wert von 60.000 EUR nicht übersteigt. In <span class="text-decoration-underline">beiden</span> Fällen benötigen wir eine Bestätigung des Steuerberaters oder einer vergleichbaren steuerberatenden Organisation mit Stempel und Unterschrift.

      p.text-preview.ml-8(v-if="readonly") {{ formData.datei_gesamtbetrag_unter_60k ? formData.datei_gesamtbetrag_unter_60k.name : '' }}
      v-file-input(
        v-else
        v-model="formData.datei_gesamtbetrag_unter_60k"
        outlined
        dense
        accept="application/pdf"
        :rules="[ rules.required, rules.valid_file_size(4), rules.valid_file(fileCodes.PROOF_TAX) ]"
        @change="(v) => onSelectFile('datei_gesamtbetrag_unter_60k', v)"
      )

    v-col.pt-0(v-if="formData.gesamtbetrag_unter_60k" cols="12")
      label.primary--text.ml-8 23.b. Steuerliche Identifikationsnummer

      p.text-preview.ml-8(v-if="readonly") {{ formData.steuer_ident_nr }}
      v-text-field.ml-8(
        v-else
        v-model="formData.steuer_ident_nr"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('steuer_ident_nr', v)"
      )

    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text 24. Ich bin als Künstler*in  hauptberuflich im folgenden Bereich tätig
        p.text-preview {{ formData.hauptberuf_bereich }}

      v-select.mt-3(
        v-else
        v-model="formData.hauptberuf_bereich"
        label="24. Ich bin als Künstler*in  hauptberuflich im folgenden Bereich tätig"
        outlined
        dense
        placeholder="Bitte Auswählen!"
        persistent-placeholder
        :items="occupations"
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('hauptberuf_bereich', v)"
      )

    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text 25. Ich bin als ausübende(r) Künstler*in im Hauptberuf ausschließlich freiberuflich / solo-selbständig ODER auf Produktionsdauer beschäftigt
        p.text-preview {{ formData.freiberuflich_solo ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.freiberuflich_solo"
        label="25. Ich bin als ausübende(r) Künstler*in im Hauptberuf ausschließlich freiberuflich / solo-selbständig ODER auf Produktionsdauer beschäftigt"
        dense
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('freiberuflich_solo', v)"
      )

    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text 26. Ich versichere, dass ich nicht Mitglied der Jury des vorliegenden Programms bin
        p.text-preview {{ formData.nicht_jury ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.nicht_jury"
        label="26. Ich versichere, dass ich nicht Mitglied der Jury des vorliegenden Programms bin"
        dense
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('nicht_jury', v)"
      )

    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text 27. Ich versichere, dass ich für das beantragte Projekt innerhalb des viermonatigen Förderzeitraums kein anderes Stipendium des Bundes, eines Landes oder einer Kommune in Anspruch nehme/nehmen werde, dessen monatlicher Förderbetrag den Betrag von 625 € übersteigt. (Bagatellgrenze)
        p.text-preview {{ formData.kein_stipendium ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.kein_stipendium"
        label="27. Ich versichere, dass ich für das beantragte Projekt innerhalb des viermonatigen Förderzeitraums kein anderes Stipendium des Bundes, eines Landes oder einer Kommune in Anspruch nehme/nehmen werde, dessen monatlicher Förderbetrag den Betrag von 625 € übersteigt. (Bagatellgrenze)"
        dense
        :rules="[ rules.required_oneof(formData.mit_stipendium) ]"
        @change="(v) => saveFieldValue('kein_stipendium', v)"
      )

    v-col(cols="12")
      template(v-if="readonly")
        label.primary--text.mtt-6 27.a. Ich erhalte noch bis zum 30.09.2021 (Laufzeitende des Stipendienprogramms) Gelder aus einem anderen Stipendienprogramms. Mir ist bekannt, dass die Auszahlung meines Stipendiums in diesem Fall erst im Oktober erfolgen wird
        p.text-preview {{ formData.mit_stipendium ? 'Ja' : 'Nein' }}

      v-checkbox(
        v-else
        v-model="formData.mit_stipendium"
        label="27.a. Ich erhalte noch bis zum 30.09.2021 (Laufzeitende des Stipendienprogramms) Gelder aus einem anderen Stipendienprogramms. Mir ist bekannt, dass die Auszahlung meines Stipendiums in diesem Fall erst im Oktober erfolgen wird"
        dense
        :rules="[ rules.required_oneof(formData.kein_stipendium) ]"
        @change="(v) => saveFieldValue('mit_stipendium', v)"
      )

    v-col(cols="12")
      label.primary--text 28. Künstlerische Vita
      p.hint-label.mb-1 Bitte listen Sie hier mit insgesamt maximal 1000 Zeichen (ohne Leerzeichen, einfacher Text ohne Textauszeichnung wie Überschriften oder Gliederung) untereinander stichwortartig die wichtigsten Stationen aus Ihrem Lebenslauf auf (Bildung/Ausbildung, künstlerischer Werdegang/Stationen, ggf. Preise, Auszeichnungen u.ä.

      p.text-preview(v-if="readonly") {{ formData.vita }}
      v-textarea(
        v-else
        v-model="formData.vita"
        outlined
        dense
        rows="5"
        :rules="[ rules.required, rules.maxLen(2000), rules.maxRows(100) ]"
        @input="(v) => saveFieldValue('vita', v)"
      )

    v-col(cols="12")
      label.primary--text 29. Konzerte, Produktionen
      p.hint-label.mb-1 Bitte listen Sie hier getrennt für 2019 und für 2020 mit insgesamt maximal 1.000 Zeichen (ohne Leerzeichen, einfacher Text) untereinander stichwortartig besonders wichtige Konzerte und/oder Produktionen auf.

      p.text-preview(v-if="readonly") {{ formData.konzerte_produktionen }}
      v-textarea(
        v-else
        v-model="formData.konzerte_produktionen"
        outlined
        dense
        rows="5"
        :rules="[ rules.required, rules.maxLen(2000), rules.maxRows(10) ]"
        @input="(v) => saveFieldValue('konzerte_produktionen', v)"
      )

    v-col(cols="12")
      label.primary--text 30. Audio-/Videobeispiel oder Online-Rezension/Pressemitteilung

      p.text-preview(v-if="readonly") {{ formData.url_referenz1 }}
      v-text-field(
        v-else
        v-model="formData.url_referenz1"
        autocomplete="off"
        outlined
        dense
        type="url"
        persistent-placeholder
        placeholder="https://example.com"
        pattern="https://.*"
        :rules="[ rules.required, rules.valid_url ]"
        @input="(v) => saveFieldValue('url_referenz1', v)"
      )

    v-col(cols="12")
      label.primary--text 31. Audio-/Videobeispiel oder Online-Rezension/Pressemitteilung (optional)

      p.text-preview(v-if="readonly") {{ formData.url_referenz2 }}
      v-text-field(
        v-else
        v-model="formData.url_referenz2"
        autocomplete="off"
        outlined
        dense
        type="url"
        persistent-placeholder
        pattern="https://.*"
        placeholder="https://example.com"
        :rules="[ rules.valid_url ]"
        @input="(v) => saveFieldValue('url_referenz2', v)"
      )

</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator'

import FormMixin from '@/mixins/form'

@Component({
  mixins: [
    FormMixin
  ],
})
export default class FundingInformation extends Vue {
  @Prop({ type: Boolean, default: false }) readonly

  occupations = [
    'Musiker Klassik*',
    'Musiker* Unterhaltungsmusik',
    'Schauspieler*',
    '(Synchron-)Sprecher*',
    'nicht eindeutig zuordenbar',
  ]

  get firstErrorField () {
    return this.getFirstErrorField('funding-information')
  }

  onSelectFile (key, v) {
    // console.log(key, v)
    this.setFileError(null)
  }
}
</script>

<style lang="sass">
label.primary--text
  font-size: 14px
</style>
